import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { useOrgStore } from '@/stores/orgStore'
import { useViewStore, PERSONAS } from '@/stores/viewStore'
import { usePublishPageData } from '@/stores/pageDataStore'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { standardsApi } from '@/api/clients/standardsApi'
import type {
  StandardOption,
  Band,
  Summary,
  FrameworkPosture,
  Waiver,
  Source,
  AssetListItem,
  TrendPoint,
  Inventory,
  SystemRisk,
  MigrationSummary as MigrationSummaryT,
  MoneyPoint,
  HndlUrgency as HndlUrgencyT,
  HndlAlgo as HndlAlgoT,
  ExpiringCerts as ExpiringCertsT,
} from '@/api/types'
import { inventoriesApi } from '@/api/clients/inventoriesApi'
import { economicsApi } from '@/api/clients/economicsApi'
import { assetsApi } from '@/api/clients/assetsApi'
import { policiesApi } from '@/api/clients/policiesApi'
import { sourcesApi } from '@/api/clients/sourcesApi'
import { PageHeader, Card } from '@/components/common/ui'
import { Modal } from '@/components/common/Modal'
import { BandBadge, ConfidenceBadge } from '@/components/common/Badge'
import { MoneyValue } from '@/components/common/MoneyValue'
import { Spinner, ErrorState, EmptyState } from '@/components/common/State'
import { DistributionDonut } from '@/components/charts/DistributionDonut'
import { TrendLine } from '@/components/charts/TrendLine'
import { MoneyTrendLine } from '@/components/charts/MoneyTrendLine'
import { RiskGauge, gaugeColor } from '@/components/charts/RiskGauge'
import { CryptoInventoryDonut } from '@/components/charts/CryptoInventoryDonut'
import { RiskRadar } from '@/components/charts/RiskRadar'
import { formatPct, formatDate } from '@/components/common/format'

const EXPOSURE_LABEL: Record<string, string> = {
  critical: 'Critical exposure',
  high: 'High exposure',
  medium: 'Elevated exposure',
  low: 'Low exposure',
  info: 'Minimal exposure',
}

// Descriptor for the *average* score (same cutoffs as gaugeColor), so the word-label
// matches the gauge number/color — not the worst-band, which is shown separately.
function scoreExposure(score: number): string {
  if (score >= 80) return EXPOSURE_LABEL.critical
  if (score >= 60) return EXPOSURE_LABEL.high
  if (score >= 40) return EXPOSURE_LABEL.medium
  if (score >= 20) return EXPOSURE_LABEL.low
  return EXPOSURE_LABEL.info
}

// Migration progress (transition_state.status) — matches the inventory page palette.
const MIGRATION_ORDER = ['completed', 'in_progress', 'not_started'] as const
const MIGRATION_HEX: Record<string, string> = {
  completed: '#22a06b',
  in_progress: '#eab308',
  not_started: '#94a3b8',
}
const MIGRATION_LABEL: Record<string, string> = {
  completed: 'Migrated',
  in_progress: 'Migrating',
  not_started: 'Not migrated',
}
type MigrationCounts = { completed: number; in_progress: number; not_started: number }

// ── Small reusable + new cards (persona layouts compose these) ──────────────────

function MigrationBar({ mig, total, className = '' }: { mig: MigrationCounts; total: number; className?: string }) {
  return (
    <div className={`flex h-2.5 w-full rounded-full overflow-hidden bg-container-light dark:bg-container-dark ${className}`}>
      {MIGRATION_ORDER.map((m) => {
        const pct = total > 0 ? (mig[m] / total) * 100 : 0
        return mig[m] > 0 ? (
          <div
            key={m}
            className="h-full"
            style={{ width: `${pct}%`, backgroundColor: MIGRATION_HEX[m] }}
            title={`${MIGRATION_LABEL[m]}: ${mig[m]} (${pct.toFixed(0)}%)`}
          />
        ) : null
      })}
    </div>
  )
}

function TopRiskySystemsCard({
  topSystems,
  mig,
  migTotal,
  pctMigrated,
  loading,
  className,
}: {
  topSystems: SystemRisk[]
  mig: MigrationCounts
  migTotal: number
  pctMigrated: number
  loading: boolean
  className?: string
}) {
  return (
    <Card title="Top risky systems" className={`overflow-hidden ${className ?? ''}`}>
      <div className="-mt-1 mb-4">
        <div className="mb-1.5 flex items-center justify-between text-xs">
          <span className="text-subtle-light dark:text-subtle-dark">Which system to fix first · migration progress</span>
          <span className="font-semibold text-text-light dark:text-text-dark">{pctMigrated}% migrated</span>
        </div>
        <MigrationBar mig={mig} total={migTotal} />
      </div>
      {loading ? (
        <Spinner />
      ) : topSystems.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-sm text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="py-2 pr-4 font-medium">Band</th>
                <th className="py-2 pr-4 font-medium">System</th>
                <th className="py-2 pr-4 font-medium hidden md:table-cell">Business unit</th>
                <th className="py-2 pr-4 font-medium">Peak</th>
                <th className="py-2 pr-4 font-medium">Critical + High</th>
                <th className="py-2 pr-2 font-medium">Assets</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {topSystems.slice(0, 7).map((sys) => (
                <tr key={sys.inventory_id || '__unassigned__'} className="hover:bg-primary/5 transition-colors">
                  <td className="py-2.5 pr-4">
                    <BandBadge band={sys.worst_band as Band} />
                  </td>
                  <td className="py-2.5 pr-4 font-medium truncate max-w-[200px]">
                    {sys.inventory_id ? (
                      <Link
                        to={`/inventories/${sys.inventory_id}?tab=risk`}
                        className="text-primary dark:text-secondary hover:underline"
                      >
                        {sys.name}
                      </Link>
                    ) : (
                      <span className="text-subtle-light dark:text-subtle-dark">{sys.name}</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-subtle-light dark:text-subtle-dark hidden md:table-cell truncate max-w-[160px]">
                    {sys.business_unit || '—'}
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className="font-bold" style={{ color: gaugeColor(sys.peak_score) }}>
                      {Math.round(sys.peak_score)}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-text-light dark:text-text-dark">{sys.crit_high}</td>
                  <td className="py-2.5 pr-2 text-subtle-light dark:text-subtle-dark">{sys.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon="inventory_2" title="No scored assets" hint="Ingest and score assets to populate." />
      )}
    </Card>
  )
}

function MigrationProgressCard({
  mig,
  migTotal,
  pctMigrated,
  className,
}: {
  mig: MigrationCounts
  migTotal: number
  pctMigrated: number
  className?: string
}) {
  return (
    <Card title="Migration progress" className={className}>
      <p className="mb-3 text-3xl font-bold text-text-light dark:text-text-dark">
        {pctMigrated}%
        <span className="ml-2 text-sm font-normal text-subtle-light dark:text-subtle-dark">
          migrated ({mig.completed} of {migTotal})
        </span>
      </p>
      <MigrationBar mig={mig} total={migTotal} className="h-4" />
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
        {MIGRATION_ORDER.map((m) => (
          <span key={m} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: MIGRATION_HEX[m] }} />
            <span className="text-text-light dark:text-text-dark">{MIGRATION_LABEL[m]}</span>
            <span className="text-subtle-light dark:text-subtle-dark">{mig[m]}</span>
          </span>
        ))}
      </div>
    </Card>
  )
}

function MigrationBySystemCard({ systems, className }: { systems: SystemRisk[]; className?: string }) {
  const rows = [...systems]
    .filter((s) => s.inventory_id)
    .map((s) => ({
      ...s,
      pct: s.count > 0 ? Math.round((s.migration.completed / s.count) * 100) : 0,
      remaining: s.count - s.migration.completed,
    }))
    .sort((a, b) => b.remaining - a.remaining)
    .slice(0, 6)
  return (
    <Card title="Migration by system" className={className}>
      {rows.length > 0 ? (
        <>
          <ul className="space-y-3">
            {rows.map((s) => (
              <li key={s.inventory_id}>
                <div className="flex items-center justify-between gap-2 text-xs mb-1">
                  <Link
                    to={`/inventories/${s.inventory_id}?tab=risk`}
                    className="truncate font-medium text-primary dark:text-secondary hover:underline"
                  >
                    {s.name}
                  </Link>
                  <span className="shrink-0 text-subtle-light dark:text-subtle-dark">
                    {s.pct}% · {s.migration.completed}/{s.count}
                  </span>
                </div>
                <MigrationBar mig={s.migration} total={s.count} />
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-3 border-t border-border-light dark:border-border-dark flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
            {MIGRATION_ORDER.map((m) => (
              <span key={m} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: MIGRATION_HEX[m] }} />
                <span className="text-subtle-light dark:text-subtle-dark">{MIGRATION_LABEL[m]}</span>
              </span>
            ))}
          </div>
        </>
      ) : (
        <EmptyState icon="donut_large" title="No systems" hint="Assign assets to inventories to track migration." />
      )}
    </Card>
  )
}

function HndlUrgencyCard({
  data,
  className,
}: {
  data: HndlUrgencyT | undefined
  className?: string
}) {
  const [drillAlgo, setDrillAlgo] = useState<HndlAlgoT | null>(null)
  const algos = data?.algos ?? []
  const earliest = data?.earliest ?? null
  const count = data?.count ?? 0
  return (
    <Card title="HNDL urgency" className={className}>
      <div className="-mt-1 mb-3 flex items-end gap-4">
        <div>
          <p className="text-3xl font-bold text-text-light dark:text-text-dark">{count}</p>
          <p className="text-xs text-subtle-light dark:text-subtle-dark">assets with an HNDL window</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-lg font-semibold" style={{ color: earliest != null && earliest <= 0 ? '#C22221' : undefined }}>
            {earliest != null ? `${earliest.toFixed(1)}y` : '—'}
          </p>
          <p className="text-xs text-subtle-light dark:text-subtle-dark">earliest window</p>
        </div>
      </div>
      {algos.length > 0 ? (
        <ul className="space-y-2 text-sm">
          {algos.map((g) => (
            <li key={`${g.algorithm}-${g.key_size}`} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate font-medium text-text-light dark:text-text-dark">
                {g.algorithm}
                {g.key_size ? ` ${g.key_size}` : ''}
                <span className="ml-1.5 font-normal text-xs text-subtle-light dark:text-subtle-dark">{g.assets} assets</span>
              </span>
              <button
                onClick={() => setDrillAlgo(g)}
                className="shrink-0 text-xs font-semibold text-primary dark:text-secondary underline decoration-dotted underline-offset-2 hover:opacity-80"
                title="View affected systems"
              >
                {g.systems_affected} system{g.systems_affected === 1 ? '' : 's'}
              </button>
              <span className={`shrink-0 w-16 text-right text-xs ${g.earliest_window <= 0 ? 'text-error' : 'text-subtle-light dark:text-subtle-dark'}`}>
                {g.earliest_window <= 0 ? 'open' : `${g.earliest_window.toFixed(1)}y`}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-subtle-light dark:text-subtle-dark">No HNDL exposure.</p>
      )}
      {drillAlgo && (
        <Modal
          title={`${drillAlgo.algorithm}${drillAlgo.key_size ? ` ${drillAlgo.key_size}` : ''} — ${drillAlgo.systems_affected} system${drillAlgo.systems_affected === 1 ? '' : 's'} affected`}
          onClose={() => setDrillAlgo(null)}
        >
          <ul className="divide-y divide-border-light dark:divide-border-dark">
            {drillAlgo.systems.map((s) => (
              <li key={s.inventory_id || s.name} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {s.inventory_id ? (
                    <Link to={`/inventories/${s.inventory_id}?tab=risk`} onClick={() => setDrillAlgo(null)} className="text-primary dark:text-secondary hover:underline">
                      {s.name}
                    </Link>
                  ) : (
                    <span className="text-subtle-light dark:text-subtle-dark">{s.name}</span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">{s.count} assets</span>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </Card>
  )
}

function ExpiringCertsCard({
  data,
  location,
  className,
}: {
  data: ExpiringCertsT | undefined
  location: ReturnType<typeof useLocation>
  className?: string
}) {
  const now = Date.now()
  const items = (data?.items ?? []).map((c) => ({
    ...c,
    days: Math.ceil((new Date(c.expiry).getTime() - now) / (24 * 60 * 60 * 1000)),
  }))
  return (
    <Card title={`Expiring certificates (${data?.count ?? 0})`} className={className}>
      {items.length > 0 ? (
        <ul className="space-y-2 text-sm">
          {items.map((c) => (
            <li key={c.id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/assets/${c.id}`}
                  state={{ backgroundLocation: location }}
                  className="block truncate text-primary dark:text-secondary hover:underline"
                >
                  {c.name}
                </Link>
                <span className="block truncate text-xs text-subtle-light dark:text-subtle-dark">
                  {c.system || 'Unassigned'}
                </span>
              </div>
              <span className={`shrink-0 text-xs ${c.days < 0 ? 'text-error' : 'text-subtle-light dark:text-subtle-dark'}`}>
                {c.days < 0 ? `expired ${-c.days}d ago` : `${c.days}d left`}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="verified" title="None within 90 days" />
      )}
    </Card>
  )
}


// PQC framework migration deadlines: derived from in-scope frameworks (crosswalk)
// + each standard's migration_deadline. "To migrate" = in-scope controls not yet safe.
function DeadlinesCard({
  crosswalk,
  standardByKey,
  className,
}: {
  crosswalk: FrameworkPosture[]
  standardByKey: (k: string) => StandardOption | undefined
  className?: string
}) {
  const now = Date.now()
  const rows = crosswalk
    .map((f) => {
      const std = standardByKey(f.framework)
      const dl = std?.migration_deadline
      if (!dl) return null
      const t = new Date(/^\d{4}$/.test(dl) ? `${dl}-01-01` : dl).getTime()
      if (Number.isNaN(t)) return null
      return {
        key: f.framework,
        name: std?.name ?? f.framework,
        year: dl.slice(0, 4),
        days: Math.ceil((t - now) / (24 * 60 * 60 * 1000)),
        toMigrate: Math.max(0, f.in_scope - f.safe),
      }
    })
    .filter((r): r is NonNullable<typeof r> => r != null)
    .sort((a, b) => a.days - b.days)
    .slice(0, 6)
  return (
    <Card title="Compliance deadlines" className={className}>
      {rows.length > 0 ? (
        <ul className="space-y-3 text-sm">
          {rows.map((d) => {
            const urgent = d.days <= 365
            return (
              <li key={d.key} className="flex items-center gap-3">
                <span className="min-w-0 flex-1 truncate font-medium text-text-light dark:text-text-dark">
                  {d.name}
                </span>
                <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">
                  {d.toMigrate} to migrate
                </span>
                <span
                  className={`shrink-0 text-xs font-semibold ${urgent ? 'text-error' : 'text-text-light dark:text-text-dark'}`}
                >
                  {d.days < 0 ? 'overdue' : `by ${d.year}`}
                </span>
              </li>
            )
          })}
        </ul>
      ) : (
        <EmptyState icon="event" title="No framework deadlines" hint="Assign frameworks with a migration deadline to an inventory." />
      )}
    </Card>
  )
}

function WaiversCard({ waivers, className }: { waivers: Waiver[]; className?: string }) {
  const active = waivers.filter((w) => w.status === 'active')
  return (
    <Card title={`Accepted risk (${active.length})`} className={className}>
      {active.length > 0 ? (
        <ul className="space-y-2 text-sm">
          {active.slice(0, 6).map((w) => (
            <li key={w.id} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-text-light dark:text-text-dark capitalize">
                {String(w.scope_type)} waiver
              </span>
              <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">exp {formatDate(w.expiry)}</span>
            </li>
          ))}
          <li>
            <Link to="/waivers" className="text-xs font-medium text-primary dark:text-secondary hover:underline">
              View all →
            </Link>
          </li>
        </ul>
      ) : (
        <EmptyState icon="gavel" title="No active waivers" hint="Residual risk under governance shows here." />
      )}
    </Card>
  )
}

function CoverageFreshnessCard({
  assetCount,
  crosswalk,
  sources,
  className,
}: {
  assetCount: number
  crosswalk: FrameworkPosture[]
  sources: Source[]
  className?: string
}) {
  const safe = crosswalk.reduce((n, f) => n + f.safe, 0)
  const inScope = crosswalk.reduce((n, f) => n + f.in_scope, 0)
  const now = Date.now()
  const staleMs = 30 * 24 * 60 * 60 * 1000
  const stale = sources.filter((s) => !s.last_ingested_at || now - new Date(s.last_ingested_at).getTime() > staleMs)
  const fresh = sources.length - stale.length

  // Labelled progress meter: filled portion = "good" ratio, color by health.
  const meter = (label: string, value: number, of: number, fillClass: string) => {
    const pct = of > 0 ? Math.round((value / of) * 100) : 0
    return (
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-medium text-text-light dark:text-text-dark">{label}</span>
          <span className="text-subtle-light dark:text-subtle-dark">
            {value}/{of} · {pct}%
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
          <div className={`h-full rounded-full ${fillClass}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>
    )
  }

  return (
    <Card title="Coverage & scan freshness" className={className}>
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-text-light dark:text-text-dark">{assetCount.toLocaleString()}</span>
        <span className="text-xs text-subtle-light dark:text-subtle-dark">assets in estate</span>
      </div>
      <div className="space-y-3">
        {meter('Framework controls safe', safe, inScope, 'bg-success')}
        {meter('Sources fresh (≤30d)', fresh, sources.length, fresh === sources.length ? 'bg-success' : 'bg-[#e8734f]')}
      </div>
      {stale.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm border-t border-border-light dark:border-border-dark pt-3">
          {stale.slice(0, 4).map((s) => (
            <li key={s.id} className="flex items-center gap-2">
              <span className="material-icons text-[16px] text-error shrink-0">history</span>
              <span className="min-w-0 flex-1 truncate text-text-light dark:text-text-dark">{s.name}</span>
              <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">
                {s.last_ingested_at ? `scanned ${formatDate(s.last_ingested_at)}` : 'never scanned'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

const DIST_BANDS: { key: keyof Summary['distribution']; label: string; hex: string }[] = [
  { key: 'critical', label: 'Critical', hex: '#C22221' },
  { key: 'high', label: 'High', hex: '#e8734f' },
  { key: 'medium', label: 'Medium', hex: '#eab308' },
  { key: 'low', label: 'Low', hex: '#94C8E4' },
  { key: 'info', label: 'Info', hex: '#94a3b8' },
]

function DistributionCard({ distribution, className }: { distribution: Summary['distribution']; className?: string }) {
  const total = DIST_BANDS.reduce((n, b) => n + distribution[b.key], 0)
  return (
    <Card title="Risk distribution" className={className}>
      <p className="-mt-1 mb-3 text-xs text-subtle-light dark:text-subtle-dark">
        Assets by risk band across the estate
      </p>
      <div className="flex items-center gap-4">
        <div className="h-40 w-40 shrink-0">
          <DistributionDonut distribution={distribution} showLegend={false} />
        </div>
        <ul className="flex-1 space-y-2 text-sm">
          {DIST_BANDS.map((b) => {
            const v = distribution[b.key]
            const pct = total > 0 ? Math.round((v / total) * 100) : 0
            return (
              <li key={b.key}>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: b.hex }} />
                  <span className="flex-1 text-text-light dark:text-text-dark">{b.label}</span>
                  <span className="font-semibold text-text-light dark:text-text-dark">{v.toLocaleString()}</span>
                  <span className="w-9 text-right text-xs text-subtle-light dark:text-subtle-dark">{pct}%</span>
                </div>
                <div className="mt-1 ml-[18px] h-1.5 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: b.hex }} />
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </Card>
  )
}

function RiskRadarCard({ axes, className }: { axes: { name: string; value: number }[]; className?: string }) {
  return (
    <Card title="Risk Radar" className={className}>
      <div className="h-56">
        <RiskRadar axes={axes} />
      </div>
      <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">Org risk across dimensions · higher = worse</p>
    </Card>
  )
}

const TREND_RANGES = [14, 30, 90] as const

function TrendCard({ trends, loading, className }: { trends: TrendPoint[] | undefined; loading: boolean; className?: string }) {
  const [days, setDays] = useState<(typeof TREND_RANGES)[number]>(90)
  const points = useMemo(() => {
    if (!trends) return []
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
    return trends.filter((p) => new Date(p.taken_at).getTime() >= cutoff)
  }, [trends, days])
  return (
    <Card title="Risk Trend" className={className}>
      <div className="-mt-1 mb-3 flex items-center justify-end gap-1">
        {TREND_RANGES.map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-2 py-0.5 text-xs rounded-md font-medium transition-colors ${
              days === d
                ? 'bg-primary/10 text-primary dark:text-secondary'
                : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>
      <div className="h-56">
        {loading ? (
          <Spinner />
        ) : points.length > 0 ? (
          <TrendLine points={points} />
        ) : (
          <EmptyState icon="show_chart" title={trends && trends.length > 0 ? `No snapshots in the last ${days} days` : 'No trend snapshots yet'} />
        )}
      </div>
    </Card>
  )
}

function MoneyTrendCard({
  points,
  currency,
  changedAt,
  loading,
  className,
}: {
  points: MoneyPoint[] | undefined
  currency?: string
  changedAt?: string | null
  loading: boolean
  className?: string
}) {
  const [days, setDays] = useState<(typeof TREND_RANGES)[number]>(90)
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  const filtered = useMemo(
    () => (points ?? []).filter((p) => new Date(p.taken_at).getTime() >= cutoff),
    [points, cutoff]
  )
  // Show the "model changed" note only when the change falls inside the visible window
  // and there are earlier points it would make incomparable.
  const changedMs = changedAt ? new Date(changedAt).getTime() : NaN
  const showChanged =
    !Number.isNaN(changedMs) &&
    changedMs >= cutoff &&
    changedMs <= Date.now() &&
    filtered.some((p) => new Date(p.taken_at).getTime() < changedMs)
  return (
    <Card title="$-at-Risk Trend" className={className}>
      <div className="-mt-1 mb-3 flex items-center justify-end gap-1">
        {TREND_RANGES.map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-2 py-0.5 text-xs rounded-md font-medium transition-colors ${
              days === d
                ? 'bg-primary/10 text-primary dark:text-secondary'
                : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>
      <div className="h-56">
        {loading ? (
          <Spinner />
        ) : filtered.length > 0 ? (
          <MoneyTrendLine points={filtered} currency={currency} />
        ) : (
          <EmptyState
            icon="show_chart"
            title={points && points.length > 0 ? `No snapshots in the last ${days} days` : 'Trend builds over time'}
          />
        )}
      </div>
      {showChanged && (
        <p className="mt-2 text-xs text-[#e8734f]">
          <span className="material-icons text-[13px] align-text-bottom">warning</span> $-model changed{' '}
          {formatDate(changedAt!)} — earlier points use the prior model and aren&apos;t directly comparable.
        </p>
      )}
    </Card>
  )
}

function CryptoInventoryCard({ assets, loading, className }: { assets: AssetListItem[] | undefined; loading: boolean; className?: string }) {
  return (
    <Card title="Cryptographic Inventory" className={className}>
      <p className="-mt-1 mb-3 text-xs text-subtle-light dark:text-subtle-dark">Algorithms in active use across the estate</p>
      {loading ? (
        <div className="h-56 grid place-items-center">
          <Spinner />
        </div>
      ) : (assets?.length ?? 0) > 0 ? (
        <div className="h-56">
          <CryptoInventoryDonut assets={assets!} />
        </div>
      ) : (
        <EmptyState icon="lock" title="No crypto assets" hint="Ingest a CBOM to inventory algorithms." />
      )}
    </Card>
  )
}

function TopMoneySystemsCard({ money, invByName, className }: { money: Summary['money']; invByName: Map<string, Inventory>; className?: string }) {
  return (
    <Card title="Top $-at-risk systems" className={className}>
      {money?.top && money.top.length > 0 ? (
        <ul className="space-y-4">
          {money.top.map((t, i) => {
            const inv = invByName.get(t.name.toLowerCase())
            const max = money.top[0]?.value || 1
            const share = Math.round((t.value / (money.total || 1)) * 100)
            return (
              <li key={t.name}>
                <div className="flex items-center gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-container-light dark:bg-container-dark text-xs font-semibold text-subtle-light dark:text-subtle-dark">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <p className="truncate text-sm font-medium text-text-light dark:text-text-dark">{t.name}</p>
                      <MoneyValue
                        value={t.value}
                        currency={money.currency}
                        className="shrink-0 text-sm font-semibold text-text-light dark:text-text-dark"
                      />
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className="truncate text-xs text-subtle-light dark:text-subtle-dark">
                        {inv?.business_unit || 'Unassigned unit'}
                      </span>
                      {inv?.criticality && <BandBadge band={inv.criticality as Band} />}
                      <span className="ml-auto shrink-0 text-xs text-subtle-light dark:text-subtle-dark">{share}% of total</span>
                    </div>
                  </div>
                </div>
                <div className="mt-2 ml-9 h-1.5 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-secondary"
                    style={{ width: `${Math.round((t.value / max) * 100)}%` }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      ) : (
        <EmptyState icon="payments" title="No economics data" hint="Author inventory economics to see $-at-risk." />
      )}
    </Card>
  )
}

function CompliancePostureCard({
  crosswalk,
  standardByKey,
  className,
}: {
  crosswalk: FrameworkPosture[]
  standardByKey: (k: string) => StandardOption | undefined
  className?: string
}) {
  return (
    <Card title="Compliance posture" className={className}>
      {crosswalk.length > 0 ? (
        <ul className="space-y-3">
          {crosswalk.map((f) => {
            const std = standardByKey(f.framework)
            const deadline = std?.migration_deadline?.slice(0, 4)
            return (
              <li key={f.framework}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-text-light dark:text-text-dark" title={std?.description}>
                    {std?.name ?? f.framework}
                    {std?.version ? (
                      <span className="ml-1 font-normal text-subtle-light dark:text-subtle-dark">{std.version}</span>
                    ) : null}
                  </span>
                  <span className="text-subtle-light dark:text-subtle-dark">
                    {formatPct(f.posture_pct)} · {f.safe}/{f.in_scope} safe{deadline ? ` · by ${deadline}` : ''}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
                  <div className="h-full bg-primary dark:bg-secondary" style={{ width: `${Math.min(100, f.posture_pct)}%` }} />
                </div>
              </li>
            )
          })}
        </ul>
      ) : (
        <EmptyState
          icon="rule"
          title="No frameworks in scope"
          hint="Assign regulatory frameworks to an inventory (Inventory → Business context → Edit)."
        />
      )}
    </Card>
  )
}

export default function Dashboard() {
  const { currentOrgId: orgId } = useOrgStore()
  const location = useLocation()
  const persona = useViewStore((st) => st.persona)

  const summary = useQuery({ queryKey: ['summary', orgId], queryFn: () => analyticsApi.summary(orgId), enabled: !!orgId })
  const trends = useQuery({ queryKey: ['trends', orgId], queryFn: () => analyticsApi.trends(orgId), enabled: !!orgId })
  const moneyTrend = useQuery({ queryKey: ['money-trend', orgId], queryFn: () => analyticsApi.moneyTrend(orgId, 30), enabled: !!orgId, retry: false })
  const moneyTrends = useQuery({ queryKey: ['money-trends', orgId], queryFn: () => analyticsApi.moneyTrends(orgId), enabled: !!orgId, retry: false })
  const econLastChanged = useQuery({ queryKey: ['econ-last-changed', orgId], queryFn: () => economicsApi.lastChanged(orgId), enabled: !!orgId, retry: false })
  const crosswalk = useQuery({ queryKey: ['crosswalk', orgId], queryFn: () => analyticsApi.crosswalk(orgId), enabled: !!orgId })
  const standards = useQuery({ queryKey: ['standards', orgId], queryFn: () => standardsApi.list(orgId), enabled: !!orgId })
  const inventories = useQuery({ queryKey: ['inventories', orgId], queryFn: () => inventoriesApi.list(orgId), enabled: !!orgId })
  const assets = useQuery({ queryKey: ['assets', orgId, 'dashboard'], queryFn: () => assetsApi.list(orgId, { limit: 500 }), enabled: !!orgId })
  const waivers = useQuery({ queryKey: ['waivers', orgId], queryFn: () => policiesApi.waivers(orgId), enabled: !!orgId, retry: false })
  const orgSources = useQuery({ queryKey: ['sources-all', orgId], queryFn: () => sourcesApi.listAll(orgId), enabled: !!orgId, retry: false })
  const migrationSummary = useQuery({ queryKey: ['migration', orgId], queryFn: () => analyticsApi.migration(orgId), enabled: !!orgId, retry: false })
  const systemsQ = useQuery({ queryKey: ['systems', orgId], queryFn: () => analyticsApi.systems(orgId), enabled: !!orgId, retry: false })
  const hndlQ = useQuery({ queryKey: ['hndl', orgId], queryFn: () => analyticsApi.hndl(orgId), enabled: !!orgId, retry: false })
  const certsQ = useQuery({ queryKey: ['expiring-certs', orgId], queryFn: () => analyticsApi.expiringCerts(orgId), enabled: !!orgId, retry: false })

  const standardByKey = useMemo(() => {
    const m = new Map<string, StandardOption>()
    for (const s of standards.data ?? []) {
      m.set(s.name.toLowerCase(), s)
      for (const a of s.aliases ?? []) m.set(a.toLowerCase(), s)
    }
    return (key: string) => m.get(key.toLowerCase())
  }, [standards.data])

  usePublishPageData(
    useMemo(
      () =>
        summary.data
          ? {
              page: 'Risk Overview dashboard',
              persona,
              asset_count: summary.data.asset_count,
              worst_band: summary.data.band,
              risk_score: summary.data.risk_score,
              distribution: summary.data.distribution,
              money: summary.data.money,
              framework_posture: crosswalk.data,
            }
          : null,
      [summary.data, crosswalk.data, persona]
    )
  )

  if (summary.isLoading) return <Spinner label="Loading risk summary…" />
  if (summary.error) return <ErrorState error={summary.error} />
  const s = summary.data!

  const invByName = new Map((inventories.data ?? []).map((inv) => [inv.name.toLowerCase(), inv]))

  // Org-wide rollups (full estate) from dedicated endpoints.
  const systems: SystemRisk[] = systemsQ.data ?? []
  const ms: MigrationSummaryT = migrationSummary.data ?? { completed: 0, in_progress: 0, not_started: 0, total: 0 }
  const orgMig = { completed: ms.completed, in_progress: ms.in_progress, not_started: ms.not_started }
  const orgMigTotal = ms.total
  const orgPctMigrated = orgMigTotal > 0 ? Math.round((orgMig.completed / orgMigTotal) * 100) : 0

  const riskScore = s.risk_score
  // Quantum-vulnerable = the actual quantum_risk posture (not yet quantum-safe), NOT
  // the risk-band histogram. safe/unknown are excluded.
  const vulnerable = s.quantum_risk.vulnerable + s.quantum_risk.partial + s.quantum_risk.hybrid
  const total = s.asset_count

  const posturePcts = (crosswalk.data ?? []).map((f) => f.posture_pct)
  const complianceGap =
    posturePcts.length > 0 ? 100 - posturePcts.reduce((sum, p) => sum + p, 0) / posturePcts.length : 0
  const concentration =
    (s.money?.total ?? 0) > 0 && s.money?.top?.length ? (s.money.top[0].value / s.money.total) * 100 : 0
  const radarAxes = [
    { name: 'Quantum Risk', value: riskScore },
    { name: 'Critical Load', value: total > 0 ? ((s.distribution.critical + s.distribution.high) / total) * 100 : 0 },
    { name: 'HNDL Urgency', value: total > 0 ? ((hndlQ.data?.count ?? 0) / total) * 100 : 0 },
    { name: 'Compliance Gap', value: complianceGap },
    { name: '$ Concentration', value: concentration },
  ]

  // Card elements the persona layouts compose.
  const el = {
    topSystems: (
      <TopRiskySystemsCard
        key="topSystems"
        topSystems={systems}
        mig={orgMig}
        migTotal={orgMigTotal}
        pctMigrated={orgPctMigrated}
        loading={systemsQ.isLoading}
        className="lg:col-span-2"
      />
    ),
    distribution: <DistributionCard key="distribution" distribution={s.distribution} />,
    hndl: <HndlUrgencyCard key="hndl" data={hndlQ.data} />,
    trend: <TrendCard key="trend" trends={trends.data} loading={trends.isLoading} className="lg:col-span-2" />,
    moneyTrend: <MoneyTrendCard key="moneyTrend" points={moneyTrends.data} currency={s.money?.currency} changedAt={econLastChanged.data?.changed_at} loading={moneyTrends.isLoading} className="lg:col-span-2" />,
    money: <TopMoneySystemsCard key="money" money={s.money} invByName={invByName} />,
    migration: <MigrationProgressCard key="migration" mig={orgMig} migTotal={orgMigTotal} pctMigrated={orgPctMigrated} />,
    radar: <RiskRadarCard key="radar" axes={radarAxes} />,
    migBySystem: <MigrationBySystemCard key="migBySystem" systems={systems} />,
    certs: <ExpiringCertsCard key="certs" data={certsQ.data} location={location} />,
    crypto: <CryptoInventoryCard key="crypto" assets={assets.data} loading={assets.isLoading} />,
    posture: <CompliancePostureCard key="posture" crosswalk={crosswalk.data ?? []} standardByKey={standardByKey} />,
    deadlines: <DeadlinesCard key="deadlines" crosswalk={crosswalk.data ?? []} standardByKey={standardByKey} />,
    waivers: <WaiversCard key="waivers" waivers={waivers.data ?? []} />,
    coverage: (
      <CoverageFreshnessCard
        key="coverage"
        assetCount={total}
        crosswalk={crosswalk.data ?? []}
        sources={orgSources.data ?? []}
      />
    ),
  }

  const layouts: Record<string, JSX.Element[]> = {
    all: [
      el.topSystems,
      el.migration,
      el.migBySystem,
      el.hndl,
      el.certs,
      el.distribution,
      el.radar,
      el.trend,
      el.moneyTrend,
      el.money,
      el.crypto,
      el.posture,
      el.deadlines,
      el.waivers,
      el.coverage,
    ],
    secmgr: [el.topSystems, el.hndl, el.distribution, el.radar],
    ciso: [el.trend, el.moneyTrend, el.money, el.migration, el.radar],
    inventory_owner: [el.topSystems, el.migBySystem, el.certs, el.crypto],
    compliance: [el.posture, el.deadlines, el.waivers, el.coverage],
    auditor: [el.coverage, el.waivers, el.posture, el.migBySystem],
  }
  const cards = layouts[persona] ?? layouts.all
  const personaLabel = PERSONAS.find((p) => p.key === persona)?.label ?? 'Complete'

  return (
    <div>
      <PageHeader title="Risk Overview" subtitle={`${personaLabel} view · portfolio-wide quantum risk posture`} />

      {/* Shared headline stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-5">
          <p className="label-sm text-subtle-light dark:text-subtle-dark">Quantum Risk Score</p>
          <div className="mt-3 flex items-center gap-4">
            <RiskGauge score={riskScore} />
            <div className="text-sm min-w-0">
              <p className="font-semibold" style={{ color: gaugeColor(riskScore) }}>
                {scoreExposure(riskScore)}
              </p>
              <p className="mt-1 text-xs text-subtle-light dark:text-subtle-dark">
                Average across all assets
              </p>
              <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">0–100 · higher = worse</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-5">
          <p className="label-sm text-subtle-light dark:text-subtle-dark">Quantum-Vulnerable Assets</p>
          <div className="mt-3 flex items-end gap-2">
            <span className="text-4xl font-bold text-text-light dark:text-text-dark">{vulnerable.toLocaleString()}</span>
            <span className="mb-1 text-sm text-subtle-light dark:text-subtle-dark">/ {total.toLocaleString()} total</span>
          </div>
          <div className="mt-3 flex h-2 w-full rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
            <div className="h-full bg-error" style={{ width: `${total > 0 ? (s.quantum_risk.vulnerable / total) * 100 : 0}%` }} />
            <div className="h-full bg-[#e8734f]" style={{ width: `${total > 0 ? (s.quantum_risk.partial / total) * 100 : 0}%` }} />
            <div className="h-full bg-warning" style={{ width: `${total > 0 ? (s.quantum_risk.hybrid / total) * 100 : 0}%` }} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-subtle-light dark:text-subtle-dark">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-error" />
              {s.quantum_risk.vulnerable.toLocaleString()} vulnerable
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#e8734f]" />
              {s.quantum_risk.partial.toLocaleString()} partial
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-warning" />
              {s.quantum_risk.hybrid.toLocaleString()} hybrid
            </span>
          </div>
          {s.overdue_count > 0 && (
            <p className="mt-2 flex items-center gap-1 text-xs font-medium text-error">
              <span className="material-icons text-sm">warning</span>
              {s.overdue_count.toLocaleString()} past PQC deadline
            </p>
          )}
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="label-sm text-subtle-light dark:text-subtle-dark">$-at-Risk</p>
            {s.money?.confidence && <ConfidenceBadge confidence={s.money.confidence} />}
          </div>
          <div className="mt-3 flex items-end gap-2">
            <MoneyValue
              value={s.money?.total ?? 0}
              currency={s.money?.currency}
              className="text-4xl font-bold text-text-light dark:text-text-dark"
            />
            <span className="mb-1 text-sm text-subtle-light dark:text-subtle-dark">total exposure</span>
          </div>
          {(() => {
            const mt = moneyTrend.data
            if (mt?.has_history && mt.delta !== 0) {
              const up = mt.delta > 0
              return (
                <p className={`mt-2 text-xs font-medium ${up ? 'text-error' : 'text-success'}`}>
                  {up ? '▲' : '▼'}{' '}
                  <MoneyValue value={Math.abs(mt.delta)} currency={mt.currency} className="font-semibold" />
                  {mt.previous !== 0 ? ` (${Math.abs(Math.round(mt.delta_pct))}%)` : ''}
                  <span className="ml-1 font-normal text-subtle-light dark:text-subtle-dark">
                    since {formatDate(mt.since)}
                  </span>
                </p>
              )
            }
            return null
          })()}
          <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
            Business value currently behind quantum-vulnerable crypto
          </p>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-5">
          <p className="label-sm text-subtle-light dark:text-subtle-dark">Critical + High</p>
          <div className="mt-3 flex items-end gap-2">
            <span className="text-4xl font-bold text-text-light dark:text-text-dark">
              {(s.distribution.critical + s.distribution.high).toLocaleString()}
            </span>
            <span className="mb-1 text-sm text-subtle-light dark:text-subtle-dark">priority</span>
          </div>
          <div className="mt-3 flex h-2 w-full rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
            <div className="h-full bg-error" style={{ width: `${total > 0 ? (s.distribution.critical / total) * 100 : 0}%` }} />
            <div className="h-full bg-[#e8734f]" style={{ width: `${total > 0 ? (s.distribution.high / total) * 100 : 0}%` }} />
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs text-subtle-light dark:text-subtle-dark">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-error" />
              {s.distribution.critical.toLocaleString()} critical
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#e8734f]" />
              {s.distribution.high.toLocaleString()} high
            </span>
          </div>
        </div>
      </div>

      {/* Persona-specific card grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 grid-flow-row-dense">{cards}</div>
    </div>
  )
}
